/*
 * pdp-stories.js — starts and stops the customer-story videos.
 *
 * It ENHANCES a lightbox that already works without it. `snippets/pdp-stories
 * .liquid` opens and closes the cards entirely in CSS: the avatar is a link to
 * a fragment and the card is `hidden target:block`. With this file missing,
 * blocked or erroring, pressing an avatar still opens the card — the reader
 * just presses play in the video's own controls, which is what the theme did
 * before this script existed. Everything below is additive.
 *
 * WHY A SCRIPT AT ALL, IN A THEME THAT SPENDS THEM CAREFULLY. There is no
 * declarative way to start a video when a fragment becomes active. `autoplay`
 * is the only markup that can start playback, it fires on page load rather
 * than on open, and it overrides `preload="none"` — the one thing this
 * component must not lose, because these clips are tens of megabytes each and
 * a PDP carries four of them. So: no bytes until a reader asks, and a script
 * to make the asking one press instead of two.
 *
 * PLAYBACK STARTS IN THE CLICK HANDLER, NOT ON `hashchange`. Safari and iOS
 * allow sound only when `play()` is called inside the gesture's own handler; a
 * `hashchange` listener runs in a later task and is refused. So the click path
 * starts the video, and `hashchange` exists for the cases where no click of
 * ours happened — a shared link with the fragment in it, or the back button.
 *
 * PAUSING ON CLOSE IS NOT A NICETY. `display: none` does not stop playback,
 * so before this file a reader who closed a card kept listening to it.
 */

/** The card wrapper for the fragment in the URL, when there is one. */
function activeCard() {
  const id = window.location.hash.slice(1)
  if (!id) return null
  /*
   * `getElementById`, not `querySelector(hash)`: section ids are Shopify's own
   * (`template--27239203373376__hero`), and a selector built from one is a
   * syntax error the moment it starts with a digit or carries a `--`.
   */
  const element = document.getElementById(id)
  return element && element.hasAttribute('data-story-card') ? element : null
}

/*
 * The video this script means to have playing. `video.paused` cannot stand in
 * for it: `play()` resolves a task later, so between the click and the
 * `hashchange` the browser fires straight after it, a video that is loading is
 * still `paused` and both paths would start it. Tracking the intent makes the
 * second call a no-op instead.
 */
let current = null

function play(video) {
  if (!video || current === video) return
  current = video
  const attempt = video.play()
  if (!attempt || typeof attempt.catch !== 'function') return
  attempt.catch(() => {
    /*
     * Refused — no gesture behind this open (a pasted link, usually), and the
     * browser will not start audio on its own. Muted playback is allowed, and
     * a playing video the reader can unmute from the controls is a better
     * answer than a still frame. If even that is refused, the card is simply
     * the paused player it has always been.
     */
    video.muted = true
    const muted = video.play()
    if (muted && typeof muted.catch === 'function') {
      muted.catch(() => {
        // Refused outright. Forget it, so pressing the avatar again retries.
        if (current === video) current = null
      })
    }
  })
}

function stop(video) {
  if (!video) return
  if (current === video) current = null
  // Already stopped at the top — nothing to pause and nothing to rewind.
  if (video.paused && video.currentTime === 0) return
  video.pause()
  /*
   * Rewind, so reopening a story starts it rather than resuming it — the
   * reference remounts the element on open and gets this for free. Setting
   * this before any data has loaded is defined behaviour: it becomes the
   * default playback start position.
   */
  video.currentTime = 0
}

/** Leaves exactly the open card's video playing, and every other one stopped. */
function sync() {
  const active = activeCard()
  for (const card of document.querySelectorAll('[data-story-card]')) {
    const video = card.querySelector('video')
    if (card === active) play(video)
    else stop(video)
  }
}

/*
 * Delegated, so cards the theme editor swaps in are covered with no re-init,
 * and so one listener serves however many strips a page ends up with.
 */
document.addEventListener('click', (event) => {
  const link = event.target.closest ? event.target.closest('a[data-story-open]') : null
  if (!link) return

  const id = (link.getAttribute('href') || '').slice(1)
  const card = id ? document.getElementById(id) : null
  if (!card || !card.hasAttribute('data-story-card')) return

  for (const other of document.querySelectorAll('[data-story-card]')) {
    if (other !== card) stop(other.querySelector('video'))
  }
  /*
   * The card is still `display: none` at this point — the fragment has not
   * changed yet. Starting a video that is not being rendered is allowed and is
   * the whole reason this runs here: it keeps the call inside the gesture.
   */
  play(card.querySelector('video'))
})

window.addEventListener('hashchange', sync)

sync()
